/**
 * v4 "Kích nét" (render_worker/sharpen.py) for both modes: the photo of the mode on screen is sent, the report shows in
 * that mode (SharpenCard), then "Dùng ảnh này" makes the sharpened photo the page's photo (`share`, keep = true: the
 * editor keeps its recipe, an ID photo keeps its choices). Owned by the page so the report stays when the mode changes.
 */
import { useEffect, useRef, useState } from "react";
import { ApiError, type Api, type Job, type Me } from "../api";
import type { AuthState } from "../components/RenderBar";
import { sharpenPrice } from "../editor/SharpenCard";
import type { EditCatalog } from "../editor/types";
import { EDIT, RENDER, errorMessage } from "../messages";
import { useJob } from "../useJob";
import type { LoginRequest, SharedPhoto } from "./types";
import { codeOf, newKey } from "./util";

export interface Sharpen {
  /** tokens one sharpen takes for this account */
  cost: number;
  job: Job | null;
  pollError: string | null;
  /** being sent */
  busy: boolean;
  /** waiting on the render machine */
  running: boolean;
  /** fetching the result to work on it */
  using: boolean;
  message: { text: string; error?: boolean } | null;
  /** the photo as it was sent (an object URL), null for a job opened from the history */
  before: string | null;
  /** sends `blob` (the mode's full photo); `blocked`: the mode's own reason not to. Returns why it did not send. */
  send: (blob: Blob | null, blocked?: string | null) => string | null;
  use: () => void;
  open: (job: Job) => void;
  clearMessage: () => void;
}

interface Deps {
  api: Api;
  pollMs: number;
  me: Me | null;
  auth: AuthState;
  consent: boolean;
  editCatalog: EditCatalog | null;
  shared: SharedPhoto | null;
  epoch: number;
  refreshMe: () => Promise<Me | null>;
  refreshJobs: () => Promise<void>;
  signedOut: () => void;
  requestLogin: (request: LoginRequest) => void;
  share: (files: File[], opts?: { keep?: boolean; sharpened?: string | null }) => number;
}

export function useSharpen(deps: Deps): Sharpen {
  const { api, pollMs } = deps;
  const [id, setId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [using, setUsing] = useState(false);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const key = useRef(newKey());
  const latest = useRef(deps);
  latest.current = deps;
  const costFor = (m: Me | null) => sharpenPrice(latest.current.editCatalog, m);
  const cost = sharpenPrice(deps.editCatalog, deps.me);
  const { job, setJob, error } = useJob(api, id, pollMs, () => {
    void latest.current.refreshMe();
    void latest.current.refreshJobs();
  });
  const running = job?.id === id && (job?.status === "queued" || job?.status === "running");

  // another photo: a new Idempotency-Key; a new photo (not a batch photo, not the sharpened one): the report goes
  const seq = deps.shared?.seq;
  const fresh = Boolean(deps.shared && !deps.shared.keep && !deps.shared.sharpened);
  useEffect(() => {
    key.current = newKey();
    if (fresh) {
      setId(null);
      setMessage(null);
    }
  }, [seq, fresh]);
  useEffect(() => {
    if (deps.epoch) setId(null);                      // signed out
  }, [deps.epoch]);
  useEffect(() => () => { if (before) URL.revokeObjectURL?.(before); }, [before]);

  function blockFor(m: Me | null, blob: Blob | null, blocked: string | null): string | null {
    if (!blob) return EDIT.needPhoto;
    if (!latest.current.consent) return EDIT.needConsent;
    if (blocked) return blocked;
    if (!m) return null;
    if (m.locked_until) return errorMessage("account_locked");
    const c = costFor(m);
    if (!m.unmetered && m.total < c) return RENDER.noCoins(c, m.total, m.daily_allowance);
    return null;
  }

  function ask(blob: Blob, blocked: string | null) {
    latest.current.requestLogin({
      forRender: true, cost: costFor(null),
      onSignedIn: (m) => {
        const block = blockFor(m, blob, blocked);
        if (block) setMessage({ text: block, error: true });
        else void sendNow(blob, costFor(m));
      },
    });
  }

  async function sendNow(blob: Blob, c: number) {
    setBusy(true);
    setMessage(null);
    try {
      const created = await api.createSharpenJob(blob, key.current);
      setBefore(URL.createObjectURL?.(blob) ?? null);
      setId(created.id);
      setJob(created);
      const m = await latest.current.refreshMe();
      setMessage({ text: created.created === false ? RENDER.replayed : EDIT.sharpen.sent(c, m?.total ?? 0) });
      void latest.current.refreshJobs();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        latest.current.signedOut();
        ask(blob, null);
        return;
      }
      const code = codeOf(e);
      if (code !== "network") key.current = newKey();
      setMessage({ text: errorMessage(code), error: true });
      void latest.current.refreshMe();
    } finally {
      setBusy(false);
    }
  }

  function send(blob: Blob | null, blocked: string | null = null): string | null {
    if (busy) return null;
    const block = blockFor(deps.me, blob, blocked);
    if (block) {
      setMessage({ text: block, error: true });
      return block;
    }
    if (!blob) return null;
    if (deps.auth !== "in") ask(blob, blocked);
    else void sendNow(blob, cost);
    return null;
  }

  /** The sharpened photo (full size) becomes the page's photo. */
  async function use() {
    const file = job?.files.export;
    if (!job || !file || using) return;
    setUsing(true);
    setMessage(null);
    try {
      const res = await fetch(api.fileUrl(file), { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      latest.current.share([new File([blob], "anh-kich-net.jpg", { type: "image/jpeg" })], { keep: true, sharpened: job.id });
    } catch (e) {
      console.warn("sharpened photo unavailable", e);
      setMessage({ text: EDIT.sharpen.useFailed, error: true });
    } finally {
      setUsing(false);
    }
  }

  function open(j: Job) {
    if (j.id !== id) setBefore(null);                 // the photo it was made from is not on this page
    setId(j.id);
  }

  return {
    cost, job, pollError: error, busy, running, using, message, before, send, open,
    use: () => void use(), clearMessage: () => setMessage(null),
  };
}
